<template lang="pug">
router-link.invoice-link(:to="{ name: 'InvoicesView', params: { id: invoice.id } }")
  AppCard.invoice-card(interactive)
    .invoice
      header.invoice__header
        .invoice__heading
          .invoice__label {{ $t("invoices.invoice") }}
          h3.invoice__folio {{ folio }}
        span.invoice__status(:class="status.variant") {{ status.label }}
      .invoice__amounts
        .invoice__amount
          h4 {{ $t("invoices.amount_total") }}
          strong {{ numberFormatter(invoice.amount_total) }}
        .invoice__amount.invoice__amount--right
          h4 {{ $t("invoices.amount_residual") }}
          strong(:class="{ 'is-pending': residual > 0 }") {{ numberFormatter(invoice.amount_residual) }}
      .invoice__due(:class="due.variant")
        i.uil(:class="due.icon" aria-hidden="true")
        span {{ due.label }}
      .invoice__grid
        .invoice__item
          h4 {{ $t("invoices.invoice_date") }}
          span {{ formatDate(invoice.invoice_date) }}
        .invoice__item
          h4 {{ $t("invoices.due_date") }}
          span {{ formatDate(invoice.invoice_date_due) }}
        .invoice__item
          h4 {{ $t("invoices.state") }}
          span {{ stateLabel }}
        .invoice__item
          h4 {{ $t("invoices.invoice_payment_term_id") }}
          span {{ paymentTerm }}
      .invoice__tags(v-if="tags.length")
        span.invoice__tag(v-for="tag in tags" :key="tag.key" :class="tag.variant") {{ tag.label }}
      footer.invoice__footer
        span {{ $t("invoices.view_detail") }}
        i.uil.uil-angle-right(aria-hidden="true")
</template>

<script>
import { computed } from "vue";

import { EMPTY_VALUE, isBlank, numberFormatter } from "@/utils/formatters";
import useInvoiceStatus from "@/composables/useInvoiceStatus";

import AppCard from "@/components/AppCard";

export default {
  components: { AppCard },
  props: {
    invoice: Object,
  },
  setup(props) {
    const folio = computed(() =>
      isBlank(props.invoice.name) ? EMPTY_VALUE : props.invoice.name
    );

    return {
      folio,
      numberFormatter,
      ...useInvoiceStatus(() => props.invoice),
    };
  },
};
</script>

<style lang="scss" scoped>
.invoice-link {
  @apply block;
  @apply h-full;
  @apply rounded-xl;
  @apply outline-none;

  &:focus-visible {
    @apply ring-2;
    @apply ring-gignetblue-300;
    @apply ring-offset-2;
  }

  &:hover .invoice__footer span {
    @apply underline;
  }
}

.invoice-card {
  @apply h-full;
}

.invoice {
  @apply flex;
  @apply flex-col;
  @apply h-full;

  &__header {
    @apply flex;
    @apply items-start;
    @apply justify-between;
    @apply gap-3;
  }

  &__heading {
    @apply min-w-0;
  }

  &__label {
    @apply text-xs;
    @apply text-bluegray-400;
    @apply uppercase;
    @apply tracking-wide;
  }

  &__folio {
    @apply text-bluegray-700;
    @apply font-semibold;
    @apply leading-snug;
    @apply truncate;
  }

  &__status {
    @apply flex-shrink-0;
    @apply px-2.5;
    @apply py-1;
    @apply rounded-full;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
    @apply whitespace-nowrap;

    &.success {
      @apply bg-gignetgreen-200;
      @apply text-gignetgreen-800;
    }
    &.info {
      @apply bg-gignetblue-100;
      @apply text-gignetblue-600;
    }
    &.warning {
      @apply bg-yellow-100;
      @apply text-yellow-700;
    }
    &.danger {
      @apply bg-red-100;
      @apply text-red-600;
    }
    &.neutral {
      @apply bg-bluegray-100;
      @apply text-bluegray-500;
    }
  }

  &__amounts {
    @apply flex;
    @apply items-end;
    @apply justify-between;
    @apply gap-3;
    @apply mt-4;
    @apply pb-4;
    @apply border-b;
    @apply border-bluegray-100;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
    }

    strong {
      @apply block;
      @apply text-xl;
      @apply font-semibold;
      @apply text-bluegray-700;
      @apply leading-tight;
      font-variant-numeric: tabular-nums;

      &.is-pending {
        @apply text-red-500;
      }
    }
  }

  &__amount--right {
    @apply text-right;

    strong {
      @apply text-base;
    }
  }

  &__due {
    @apply flex;
    @apply items-center;
    @apply gap-1.5;
    @apply mt-3;
    @apply text-xs;
    @apply text-bluegray-500;

    i {
      @apply text-base;
      @apply leading-none;
      @apply text-bluegray-300;
    }

    &.is-overdue {
      @apply text-red-500;
      @apply font-medium;
      i {
        @apply text-red-400;
      }
    }

    &.is-paid {
      @apply text-gignetgreen-700;
      i {
        @apply text-gignetgreen-500;
      }
    }
  }

  &__grid {
    @apply grid;
    @apply grid-cols-2;
    @apply gap-x-4;
    @apply gap-y-3;
    @apply mt-4;
  }

  &__item {
    @apply min-w-0;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
      @apply truncate;
    }

    span {
      @apply block;
      @apply text-sm;
      @apply text-bluegray-700;
      @apply truncate;
    }
  }

  &__tags {
    @apply flex;
    @apply flex-wrap;
    @apply gap-2;
    @apply mt-4;
  }

  &__tag {
    @apply px-2;
    @apply py-1;
    @apply rounded-md;
    @apply bg-bluegray-100;
    @apply text-bluegray-600;
    @apply text-xs;
    @apply font-medium;
    @apply leading-none;

    &.danger {
      @apply bg-red-50;
      @apply text-red-500;
    }
  }

  &__footer {
    @apply flex;
    @apply items-center;
    @apply justify-end;
    @apply mt-auto;
    @apply pt-4;
    @apply text-sm;
    @apply font-medium;
    @apply text-gignetblue-400;

    i {
      @apply text-xl;
      @apply leading-none;
    }
  }
}
</style>
